import React, { useState, useRef, useEffect, useCallback } from "react";
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Image as ImageIcon,
  Eye,
  Calendar,
  User,
  Loader2
} from "lucide-react";
import { toast } from "react-hot-toast";
import Button from "./Button";
import Input from "./Input";
import { blogPostService } from "../services/blogPostService";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  featured_image?: string;
  status: "draft" | "published";
  created_at: string;
  updated_at: string;
  tags: string[];
  slug: string;
  views: number;
}

interface BlogPageProps {}

const BlogPage: React.FC<BlogPageProps> = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    author: "",
    featured_image: "",
    status: "draft" as "draft" | "published",
    tags: ""
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPosts = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await blogPostService.getPosts({ 
        page, 
        per_page: pagination.per_page 
      });
      
      const mappedPosts: BlogPost[] = response.data.map(post => ({
        id: post.id,
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        author: post.author,
        featured_image: post.featured_image,
        status: post.status,
        created_at: post.created_at,
        updated_at: post.updated_at,
        tags: post.tags,
        slug: post.slug,
        views: post.views
      }));
      
      setPosts(mappedPosts);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        per_page: response.per_page,
        total: response.total
      });
    } catch (error) {
      toast.error("Failed to load posts");
      console.error("Error loading posts:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.per_page]);

  // Load posts on component mount
  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const dataUrl = event.target.result as string;
          setImagePreview(dataUrl);
          setFormData(prev => ({ ...prev, featured_image: dataUrl }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      excerpt: "",
      author: "",
      featured_image: "",
      status: "draft",
      tags: ""
    });
    setImagePreview(null);
    setIsCreating(false);
    setEditingPost(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    try {
      setSubmitting(true);

      const postData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt || formData.content.substring(0, 150) + "...",
        author: formData.author || "Admin",
        featured_image: formData.featured_image,
        status: formData.status,
        tags: formData.tags.split(",").map(tag => tag.trim()).filter(tag => tag),
      };

      if (editingPost) {
        await blogPostService.updatePost(editingPost.id, postData);
        toast.success("Post updated successfully!");
      } else {
        await blogPostService.createPost(postData);
        toast.success("Post created successfully!");
      }

      resetForm();
      await loadPosts(); // Reload posts
    } catch (error) {
      toast.error(editingPost ? "Failed to update post" : "Failed to create post");
      console.error("Error submitting post:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      author: post.author,
      featured_image: post.featured_image || "",
      status: post.status,
      tags: post.tags.join(", ")
    });
    setImagePreview(post.featured_image || null);
    setIsCreating(true);
  };

  const handleDelete = async (postId: string) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await blogPostService.deletePost(postId);
        toast.success("Post deleted successfully!");
        await loadPosts(); // Reload posts
      } catch (error) {
        toast.error("Failed to delete post");
        console.error("Error deleting post:", error);
      }
    }
  };

  const toggleStatus = async (postId: string, currentStatus: string) => {
    try {
      if (currentStatus === "published") {
        await blogPostService.unpublishPost(postId);
      } else {
        await blogPostService.publishPost(postId);
      }
      toast.success("Post status updated!");
      await loadPosts(); // Reload posts
    } catch (error) {
      toast.error("Failed to update post status");
      console.error("Error updating post status:", error);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            Blog Management
          </h1>
          <p className="text-gray-600 mt-2">Create and manage blog posts</p>
        </div>
        
        {!isCreating && (
          <Button 
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Post
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingPost ? "Edit Post" : "Create New Post"}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title and Author Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                label="Title *"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Enter post title"
              />
              
              <Input
                type="text"
                label="Author"
                value={formData.author}
                onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                placeholder="Author name (default: Admin)"
              />
            </div>

            {/* Featured Image */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Featured Image
              </label>
              <div className="flex items-center space-x-4">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 flex items-center justify-center overflow-hidden"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                      <span className="text-xs text-gray-500">Upload Image</span>
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                <div className="flex-1">
                  <Input
                    type="url"
                    label=""
                    value={formData.featured_image}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, featured_image: e.target.value }));
                      setImagePreview(e.target.value || null);
                    }}
                    placeholder="Or paste image URL"
                  />
                </div>
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Excerpt
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Short description (optional - will auto-generate from content)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your blog post content here..."
                required
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Tags and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                label="Tags"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="Separate tags with commas"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as "draft" | "published" }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={resetForm}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex items-center gap-2" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {editingPost ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {editingPost ? "Update Post" : "Create Post"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Posts List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Blog Posts ({pagination.total})
          </h2>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-500">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No blog posts yet</h3>
            <p className="text-gray-500 mb-4">Create your first blog post to get started</p>
            <Button onClick={() => setIsCreating(true)} className="flex items-center gap-2 mx-auto">
              <Plus className="h-4 w-4" />
              Create First Post
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {posts.map((post) => (
              <div key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {post.featured_image && (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{post.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            post.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {post.status}
                          </span>
                          <span className="text-xs">
                            {post.views} views
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{post.excerpt}</p>
                    
                    {/* Post Content Preview */}
                    {post.content && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-200">
                        <p className="text-sm text-gray-500 mb-1 font-medium">Content Preview:</p>
                        <p className="text-gray-700 text-sm" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {post.content.length > 200 
                            ? `${post.content.substring(0, 200)}...` 
                            : post.content
                          }
                        </p>
                      </div>
                    )}
                    
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => toggleStatus(post.id, post.status)}
                      className={`p-2 rounded-md ${
                        post.status === 'published'
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-yellow-600 hover:bg-yellow-50'
                      }`}
                      title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(post)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="p-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} posts
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => loadPosts(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => loadPosts(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
